import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../context/CompanyContext';
import {
  Plus, Search, Pencil, Trash2, Loader2, Users as UsersIcon, MoreHorizontal,
  DollarSign, Briefcase, TrendingUp, BarChart3, Target, ChevronsUpDown, Check,
  PieChart as PieIcon, Activity,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
} from 'recharts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../components/ui/command';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtCurrency = (v) =>
  v == null ? '—' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
const fmtCompact = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 }).format(v || 0);
const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(2)}%`);

function StatCard({ label, value, icon: Icon, accent = 'cyan', sublabel }) {
  const accents = {
    cyan: 'bg-cyan-50 text-cyan-600',
    green: 'bg-emerald-50 text-emerald-600',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-semibold mt-1">{value}</p>
          {sublabel && <p className="text-xs text-gray-400 mt-1">{sublabel}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accents[accent]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

const emptyForm = {
  client_name: '', email: '', phone: '',
  contract_start_date: '', contract_end_date: '',
  contract_amount: '', contract_fee: '',
  target_return: '', current_return: '',
  deposit_balance: '', aum: '',
  payment_schedule: '', status: 'active',
  notes: '', user_id: '',
};

export default function PraxisClientsPage() {
  const { currentCompany } = useCompany();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [switcherOpen, setSwitcherOpen] = useState(false);

  const fetchData = async () => {
    if (!currentCompany) return;
    setLoading(true);
    try {
      const params = { company_id: currentCompany.id };
      if (search) params.search = search;
      const [clientsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/api/praxis/clients`, { params }),
        axios.get(`${API_URL}/api/admin/users`).catch(() => ({ data: [] })),
      ]);
      setClients(clientsRes.data);
      setUsers((usersRes.data || []).filter((u) => u.role === 'client'));
    } catch {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.id, search]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      client_name: c.client_name || '',
      email: c.email || '',
      phone: c.phone || '',
      contract_start_date: c.contract_start_date || '',
      contract_end_date: c.contract_end_date || '',
      contract_amount: c.contract_amount != null ? String(c.contract_amount) : '',
      contract_fee: c.contract_fee != null ? String(c.contract_fee) : '',
      target_return: c.target_return != null ? String(c.target_return) : '',
      current_return: c.current_return != null ? String(c.current_return) : '',
      deposit_balance: c.deposit_balance != null ? String(c.deposit_balance) : '',
      aum: c.aum != null ? String(c.aum) : '',
      payment_schedule: c.payment_schedule || '',
      status: c.status || 'active',
      notes: c.notes || '',
      user_id: c.user_id || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.client_name.trim()) {
      toast.error('Client name is required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        company_id: currentCompany.id,
        ...form,
        user_id: form.user_id || null,
      };
      if (editing) {
        await axios.put(`${API_URL}/api/praxis/clients/${editing.id}`, payload);
        toast.success('Client updated');
      } else {
        await axios.post(`${API_URL}/api/praxis/clients`, payload);
        toast.success('Client added');
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/api/praxis/clients/${deleteTarget.id}`);
      toast.success('Client deleted');
      setDeleteTarget(null);
      fetchData();
    } catch {
      toast.error('Failed to delete');
    }
  };

  if (!currentCompany) return null;

  // Aggregate stats across all clients
  const totalAUM = clients.reduce((s, c) => s + (c.aum || 0), 0);
  const totalContract = clients.reduce((s, c) => s + (c.contract_amount || 0), 0);
  const totalDepositBal = clients.reduce((s, c) => s + (c.deposit_balance || 0), 0);
  const returns = clients.filter((c) => c.current_return != null).map((c) => c.current_return);
  const avgReturn = returns.length ? returns.reduce((a, b) => a + b, 0) / returns.length : 0;
  const activeCount = clients.filter((c) => c.status === 'active').length;

  // Per-client mini summary for chart (top 10 by AUM)
  const sortedByAum = [...clients].sort((a, b) => (b.aum || 0) - (a.aum || 0)).slice(0, 10);

  const statusSplit = [
    { name: 'Active', value: clients.filter((c) => c.status === 'active').length },
    { name: 'Inactive', value: clients.filter((c) => c.status === 'inactive').length },
    { name: 'Closed', value: clients.filter((c) => c.status === 'closed').length },
  ].filter((x) => x.value > 0);
  const STATUS_COLORS = ['#06B6D4', '#A1A1AA', '#EF4444'];

  return (
    <div className="space-y-6" data-testid="praxis-clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Praxis Clients</h1>
          <p className="text-gray-500 mt-1">Manage Praxis client contracts, AUM, and performance</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Searchable client switcher → jumps to the client detail */}
          {clients.length > 0 && (
            <Popover open={switcherOpen} onOpenChange={setSwitcherOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={switcherOpen}
                  className="w-[260px] justify-between"
                  data-testid="open-client-portal-switcher"
                >
                  <span className="flex items-center gap-2 text-gray-600">
                    <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <span>Open client portal…</span>
                  </span>
                  <ChevronsUpDown className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[260px] p-0" align="end">
                <Command>
                  <CommandInput placeholder="Search clients..." data-testid="portal-switcher-input" />
                  <CommandList>
                    <CommandEmpty>No client found.</CommandEmpty>
                    <CommandGroup>
                      {clients.map((c) => (
                        <CommandItem
                          key={c.id}
                          value={`${c.client_name} ${c.email || ''}`}
                          onSelect={() => { setSwitcherOpen(false); navigate(`/praxis/clients/${c.id}`); }}
                          data-testid={`portal-switcher-option-${c.id}`}
                          className="cursor-pointer"
                        >
                          <Check className="mr-2 h-4 w-4 opacity-0" />
                          <div className="flex flex-col">
                            <span>{c.client_name}</span>
                            {c.email && <span className="text-xs text-gray-500">{c.email}</span>}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
          <Button onClick={openAdd} className="bg-cyan-600 hover:bg-cyan-700" data-testid="add-praxis-client-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Client
          </Button>
        </div>
      </div>

      {/* Aggregate stat cards */}
      {clients.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Total Clients" value={clients.length} sublabel={`${activeCount} active`} icon={UsersIcon} accent="cyan" />
          <StatCard label="Total AUM" value={fmtCompact(totalAUM)} icon={DollarSign} accent="green" />
          <StatCard label="Contract Total" value={fmtCompact(totalContract)} icon={Briefcase} accent="purple" />
          <StatCard label="Average Return" value={`${avgReturn.toFixed(2)}%`} icon={TrendingUp} accent="amber" />
        </div>
      )}

      {/* Charts: AUM by client + Status split */}
      {clients.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-4 border-b border-gray-100 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-cyan-600" />
              <h2 className="font-medium text-gray-900">AUM by Client (Top {Math.min(10, sortedByAum.length)})</h2>
            </div>
            <div className="p-4">
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={sortedByAum} margin={{ top: 5, right: 16, left: 0, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="client_name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" interval={0} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtCompact} />
                  <Tooltip formatter={(v) => fmtCurrency(v)} />
                  <Bar dataKey="aum" name="AUM" radius={[4, 4, 0, 0]}>
                    {sortedByAum.map((_, i) => <Cell key={i} fill="#06B6D4" />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="p-4 border-b border-gray-100 flex items-center gap-2">
              <PieIcon className="w-4 h-4 text-cyan-600" />
              <h2 className="font-medium text-gray-900">Status Split</h2>
            </div>
            <div className="p-4">
              {statusSplit.length === 0 ? (
                <div className="h-[300px] flex items-center justify-center text-sm text-gray-400">No data.</div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={statusSplit} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={(d) => d.value}>
                      {statusSplit.map((_, i) => <Cell key={i} fill={STATUS_COLORS[i % STATUS_COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Returns vs Target chart */}
      {clients.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
            <Target className="w-4 h-4 text-cyan-600" />
            <h2 className="font-medium text-gray-900">Returns vs Target</h2>
          </div>
          <div className="p-4">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={sortedByAum} margin={{ top: 5, right: 16, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="client_name" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" interval={0} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Legend />
                <Bar dataKey="current_return" name="Current" fill="#06B6D4" radius={[4, 4, 0, 0]} />
                <Bar dataKey="target_return" name="Target" fill="#A1A1AA" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Search bar for filtering the table below */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Search by name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="praxis-clients-search"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : clients.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <UsersIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No clients yet</h3>
          <Button onClick={openAdd} className="bg-cyan-600 hover:bg-cyan-700 mt-2">
            <Plus className="w-4 h-4 mr-2" /> Add Client
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Contract</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">AUM</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Return</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Schedule</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Linked Login</th>
                <th className="px-4 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {clients.map((c) => {
                const linkedUser = users.find((u) => u.id === c.user_id);
                return (
                  <tr key={c.id} className="hover:bg-gray-50" data-testid={`praxis-row-${c.id}`}>
                    <td className="px-4 py-2.5 text-sm">
                      <button
                        onClick={() => navigate(`/praxis/clients/${c.id}`)}
                        className="font-medium text-cyan-700 hover:underline"
                      >
                        {c.client_name}
                      </button>
                      {c.email && <p className="text-xs text-gray-500">{c.email}</p>}
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          c.status === 'active'
                            ? 'bg-emerald-100 text-emerald-700'
                            : c.status === 'closed'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {c.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-sm text-right">{fmtCurrency(c.contract_amount)}</td>
                    <td className="px-4 py-2.5 text-sm text-right font-medium">{fmtCurrency(c.aum)}</td>
                    <td className="px-4 py-2.5 text-sm text-right text-cyan-700">{fmtPct(c.current_return)}</td>
                    <td className="px-4 py-2.5 text-sm capitalize text-gray-600">{c.payment_schedule || '—'}</td>
                    <td className="px-4 py-2.5 text-sm">
                      {linkedUser ? (
                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                          @{linkedUser.username}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/praxis/clients/${c.id}`)}>
                            View details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEdit(c)}>
                            <Pencil className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDeleteTarget(c)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Praxis Client' : 'Add Praxis Client'}</DialogTitle>
            <DialogDescription>Track contract, AUM, and performance details for this client.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2 space-y-1.5">
              <Label>Client Name *</Label>
              <Input value={form.client_name} onChange={(e) => setForm((p) => ({ ...p, client_name: e.target.value }))} data-testid="praxis-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Contract Start</Label>
              <Input type="date" value={form.contract_start_date} onChange={(e) => setForm((p) => ({ ...p, contract_start_date: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Contract End</Label>
              <Input type="date" value={form.contract_end_date} onChange={(e) => setForm((p) => ({ ...p, contract_end_date: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Contract Amount ($)</Label>
              <Input type="number" step="0.01" value={form.contract_amount} onChange={(e) => setForm((p) => ({ ...p, contract_amount: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Contract Fee ($)</Label>
              <Input type="number" step="0.01" value={form.contract_fee} onChange={(e) => setForm((p) => ({ ...p, contract_fee: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Target Return (%)</Label>
              <Input type="number" step="0.01" value={form.target_return} onChange={(e) => setForm((p) => ({ ...p, target_return: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Current Return (%)</Label>
              <Input type="number" step="0.01" value={form.current_return} onChange={(e) => setForm((p) => ({ ...p, current_return: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Deposit Balance ($)</Label>
              <Input type="number" step="0.01" value={form.deposit_balance} onChange={(e) => setForm((p) => ({ ...p, deposit_balance: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>AUM ($)</Label>
              <Input type="number" step="0.01" value={form.aum} onChange={(e) => setForm((p) => ({ ...p, aum: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Payment Schedule</Label>
              <Select value={form.payment_schedule} onValueChange={(v) => setForm((p) => ({ ...p, payment_schedule: v }))}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="semi-annual">Semi-annual</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((p) => ({ ...p, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Linked Login (client role users only)</Label>
              <Select value={form.user_id || 'none'} onValueChange={(v) => setForm((p) => ({ ...p, user_id: v === 'none' ? '' : v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="None — admin only access" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None — admin only access</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      @{u.username} ({u.name})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500">
                Create a Client-role user first in Settings → Users, then link it here so they can log in and see only this client record.
              </p>
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-cyan-600 hover:bg-cyan-700" data-testid="save-praxis-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? 'Save changes' : 'Add Client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete client</AlertDialogTitle>
            <AlertDialogDescription>
              Delete "{deleteTarget?.client_name}"? Transactions stay in the database but become inaccessible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
